/**
 * Staff Authentication Hook
 * Manages authentication state and role-based access for staff dashboards
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';


export function useStaffAuth(requiredRoles = []) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check both possible token storage keys for compatibility
      const token = localStorage.getItem('token') || localStorage.getItem('easystay_token');
      
      if (!token) {
        // No token, redirect to login
        toast.error('Please log in to access staff dashboard');
        navigate('/login');
        return;
      }

      // Verify token and get user info
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Authentication failed');
      }

      const data = await response.json();
      
      // Check if user has required role
      const userRole = data.role;
      
      if (requiredRoles.length > 0 && !requiredRoles.includes(userRole)) {
        toast.error(`Access denied. This dashboard is for ${requiredRoles.join(', ')} staff only.`);
        setHasAccess(false);
        setLoading(false);
        
        // Redirect to appropriate dashboard based on their actual role
        redirectToRoleDashboard(userRole);
        return;
      }

      setUser(data);
      setHasAccess(true);
      setLoading(false);
    } catch (error) {
      console.error('Auth check failed:', error);
      toast.error('Session expired. Please log in again.');
      localStorage.removeItem('token');
      navigate('/login');
    }
  };

  const redirectToRoleDashboard = (role) => {
    switch (role) {
      case 'housekeeping':
        navigate('/housekeeping');
        break;
      case 'maintenance':
        navigate('/maintenance');
        break;
      case 'front_desk':
        navigate('/checkin'); // or create /front-desk
        break;
      case 'manager':
      case 'owner':
        navigate('/dashboard');
        break;
      default:
        navigate('/login');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setHasAccess(false);
    toast.success('Logged out successfully');
    navigate('/login');
  };

  return {
    user,
    loading,
    hasAccess,
    logout,
    hotelId: user?.hotel_id
  };
}

/**
 * Check if current user has specific role
 */
export function useHasRole(allowedRoles = []) {
  const [hasRole, setHasRole] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkRole();
  }, []);

  const checkRole = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setHasRole(false);
        setLoading(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        setHasRole(false);
        setLoading(false);
        return;
      }

      const data = await response.json();
      const userRole = data.role;

      setHasRole(allowedRoles.length === 0 || allowedRoles.includes(userRole));
      setLoading(false);
    } catch (error) {
      console.error('Role check failed:', error);
      setHasRole(false);
      setLoading(false);
    }
  };

  return { hasRole, loading };
}
